import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@dukene/shared';
import {
    Users,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
    Search,
    Mail,
    Calendar
} from 'lucide-react';

interface Affiliate {
    id: string;
    userId: string;
    displayName: string;
    bio?: string;
    socialMedia?: string[];
    status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'SUSPENDED';
    totalClicks: number;
    totalSales: number;
    totalEarnings: number;
    createdAt: string;
    approvedAt?: string;
    rejectionReason?: string;
    user: {
        email: string;
        firstName: string;
        lastName: string;
    };
}

interface PaginatedResponse {
    items: Affiliate[];
    pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    };
}

const statusConfig = {
    PENDING: {
        label: 'Pending',
        icon: Clock,
        className: 'bg-gray-100 text-gray-800 border-gray-300',
    },
    APPROVED: {
        label: 'Approved',
        icon: CheckCircle,
        className: 'bg-gray-200 text-gray-900 border-gray-400',
    },
    REJECTED: {
        label: 'Rejected',
        icon: XCircle,
        className: 'bg-gray-50 text-gray-700 border-gray-200',
    },
    SUSPENDED: {
        label: 'Suspended',
        icon: AlertCircle,
        className: 'bg-white text-gray-600 border-gray-300',
    },
};

export default function Affiliates() {
    const navigate = useNavigate();
    const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [stats, setStats] = useState({
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
    });

    const fetchAffiliates = async () => {
        try {
            setLoading(true);
            setError('');

            const params = new URLSearchParams({
                page: page.toString(),
                limit: '20',
                ...(search && { search }),
                ...(statusFilter !== 'all' && { status: statusFilter }),
            });

            const result = await apiClient.get<PaginatedResponse>(`/affiliate/admin/all?${params}`);
            
            setAffiliates(result.items);
            setTotalPages(result.pagination.totalPages);

            setStats({
                total: result.pagination.total,
                pending: result.items.filter(a => a.status === 'PENDING').length,
                approved: result.items.filter(a => a.status === 'APPROVED').length,
                rejected: result.items.filter(a => a.status === 'REJECTED').length,
            });
        } catch (err: any) {
            setError(err.message || 'Failed to load affiliates');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAffiliates();
    }, [page, statusFilter]);

    const handleApprove = async (affiliateId: string) => {
        try {
            await apiClient.patch(`/affiliate/admin/${affiliateId}/approve`);
            fetchAffiliates();
        } catch (err: any) {
            alert(err.message || 'Failed to approve affiliate');
        }
    };

    const handleReject = async (affiliateId: string) => {
        const reason = prompt('Please enter rejection reason:');
        if (!reason) return;

        try {
            await apiClient.patch(`/affiliate/admin/${affiliateId}/reject`, { rejectionReason: reason });
            fetchAffiliates();
        } catch (err: any) {
            alert(err.message || 'Failed to reject affiliate');
        }
    };

    const handleSuspend = async (affiliateId: string) => {
        if (!confirm('Are you sure you want to suspend this affiliate?')) return;

        try {
            await apiClient.patch(`/affiliate/admin/${affiliateId}/suspend`);
            fetchAffiliates();
        } catch (err: any) {
            alert(err.message || 'Failed to suspend affiliate');
        }
    };

    const handleSearch = () => {
        setPage(1);
        fetchAffiliates();
    };

    const handleRowClick = (affiliateId: string) => {
        navigate(`/affiliates/${affiliateId}`);
    };

    return (
        <div className="p-8">
            {/* Header */}
            <div className="mb-8 flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Affiliate Management</h1>
                    <p className="text-gray-600">Manage and approve affiliate applications</p>
                </div>
                <button
                    onClick={() => navigate('/affiliates/create')}
                    className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2 font-medium"
                >
                    <Users className="w-5 h-5" />
                    Create Affiliate
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600 mb-1">Total Affiliates</p>
                            {loading ? (
                                <div className="h-8 w-16 bg-gray-200 rounded animate-pulse"></div>
                            ) : (
                                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                            )}
                        </div>
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Users className="w-6 h-6 text-gray-700" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600 mb-1">Pending Approval</p>
                            {loading ? (
                                <div className="h-8 w-16 bg-gray-200 rounded animate-pulse"></div>
                            ) : (
                                <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
                            )}
                        </div>
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Clock className="w-6 h-6 text-gray-700" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600 mb-1">Approved</p>
                            {loading ? (
                                <div className="h-8 w-16 bg-gray-200 rounded animate-pulse"></div>
                            ) : (
                                <p className="text-2xl font-bold text-gray-900">{stats.approved}</p>
                            )}
                        </div>
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                            <CheckCircle className="w-6 h-6 text-gray-700" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600 mb-1">Rejected</p>
                            {loading ? (
                                <div className="h-8 w-16 bg-gray-200 rounded animate-pulse"></div>
                            ) : (
                                <p className="text-2xl font-bold text-gray-900">{stats.rejected}</p>
                            )}
                        </div>
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                            <XCircle className="w-6 h-6 text-gray-700" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
                <div className="flex flex-col md:flex-row gap-4">
                    {/* Search */}
                    <div className="flex-1">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by name or email..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    {/* Status Filter */}
                    <div className="w-full md:w-48">
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="all">All Status</option>
                            <option value="PENDING">Pending</option>
                            <option value="APPROVED">Approved</option>
                            <option value="REJECTED">Rejected</option>
                            <option value="SUSPENDED">Suspended</option>
                        </select>
                    </div>

                    {/* Search Button */}
                    <button
                        onClick={handleSearch}
                        className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                    >
                        Search
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {loading ? (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Affiliate</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Performance</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 animate-pulse">
                                {[...Array(10)].map((_, i) => (
                                    <tr key={i}>
                                        <td className="px-3 py-2">
                                            <div className="h-4 bg-gray-200 rounded w-32 mb-1"></div>
                                            <div className="h-3 bg-gray-200 rounded w-24"></div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="h-3 bg-gray-200 rounded w-40 mb-1"></div>
                                            <div className="h-3 bg-gray-200 rounded w-20"></div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="h-3 bg-gray-200 rounded w-16 mb-1"></div>
                                            <div className="h-3 bg-gray-200 rounded w-20"></div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="h-3 bg-gray-200 rounded w-24"></div>
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <div className="h-6 bg-gray-200 rounded w-16"></div>
                                                <div className="h-6 bg-gray-200 rounded w-14"></div>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : error ? (
                    <div className="p-12 text-center">
                        <XCircle className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                        <p className="text-gray-900">{error}</p>
                    </div>
                ) : affiliates.length === 0 ? (
                    <div className="p-12 text-center">
                        <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600">No affiliates found</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Affiliate
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Contact
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Performance
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Joined
                                    </th>
                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {affiliates.map((affiliate) => {
                                    const StatusIcon = (statusConfig as any)[affiliate.status]?.icon || AlertCircle;
                                    return (
                                        <tr
                                            key={affiliate.id}
                                            onClick={() => handleRowClick(affiliate.id)}
                                            className="hover:bg-gray-50 cursor-pointer"
                                        >
                                            <td className="px-3 py-2">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">{affiliate.displayName}</div>
                                                    <div className="text-xs text-gray-500">
                                                        {affiliate.user.firstName} {affiliate.user.lastName}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="space-y-0.5">
                                                    <div className="flex items-center gap-1 text-xs text-gray-600">
                                                        <Mail className="w-3 h-3" />
                                                        <span className="truncate max-w-[200px]">{affiliate.user.email}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${statusConfig[affiliate.status].className}`}>
                                                    <StatusIcon className="w-3 h-3" />
                                                    {statusConfig[affiliate.status].label}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="text-xs space-y-0.5">
                                                    <div className="text-gray-900 font-medium">{affiliate.totalSales} sales</div>
                                                    <div className="text-gray-500">KES {affiliate.totalEarnings.toLocaleString()}</div>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="flex items-center gap-1 text-xs text-gray-600">
                                                    <Calendar className="w-3 h-3" />
                                                    <span className="whitespace-nowrap">{new Date(affiliate.createdAt).toLocaleDateString()}</span>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    {affiliate.status === 'PENDING' && (
                                                        <>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleApprove(affiliate.id); }}
                                                                className="px-2 py-1 bg-gray-900 text-white text-xs rounded hover:bg-gray-800 transition-colors"
                                                            >
                                                                Approve
                                                            </button>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleReject(affiliate.id); }}
                                                                className="px-2 py-1 bg-gray-700 text-white text-xs rounded hover:bg-gray-600 transition-colors"
                                                            >
                                                                Reject
                                                            </button>
                                                        </>
                                                    )}
                                                    {affiliate.status === 'APPROVED' && (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleSuspend(affiliate.id); }}
                                                            className="px-2 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-500 transition-colors"
                                                        >
                                                            Suspend
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>
                        <span className="text-sm text-gray-700">
                            Page {page} of {totalPages}
                        </span>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
